import { action, observable, makeObservable } from 'mobx';
import { Model, IModelAttributes, attribute, entity } from 'Models/Model';
import * as Models from 'Models/Entities';
/* eslint-disable max-len */

export interface IAddOnsPriceEntityAttributes extends IModelAttributes {
	price: number;

	additionalBookingOptionId: string;
	additionalBookingOption: Models.AdditionalBookingOptionEntity | Models.IAdditionalBookingOptionEntityAttributes;
	defaultPricingId?: string;
	defaultPricing?: Models.DefaultPricingEntity | Models.IDefaultPricingEntityAttributes;
	userId?: string;
	user?: Models.UserEntity | Models.IUserEntityAttributes;
}
@entity('AddOnsPriceEntity', 'Add Ons Price')
export default class AddOnsPriceEntity extends Model implements IAddOnsPriceEntityAttributes {
	/**
	 * Fields to exclude from the JSON serialization in create operations.
	 */
	public static excludeFromCreate: string[] = [];

	/**
	 * Fields to exclude from the JSON serialization in update operations.
	 */
	public static excludeFromUpdate: string[] = [];

	@observable
	@attribute<AddOnsPriceEntity, number>()
	public price: number;

	@observable
	@attribute()
	public additionalBookingOptionId: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public additionalBookingOption: Models.AdditionalBookingOptionEntity;

	@observable
	@attribute()
	public defaultPricingId?: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public defaultPricing: Models.DefaultPricingEntity;

	@observable
	@attribute()
	public userId?: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public user: Models.UserEntity;

	constructor(attributes?: Partial<IAddOnsPriceEntityAttributes>) {
		super(attributes);

		makeObservable(this);

		this.assignAttributes(attributes);
	}

	/**
	 * Assigns fields from a passed in JSON object to the fields in this model.
	 * Any reference objects that are passed in are converted to models if they are not already.
	 * This function is called from the constructor to assign the initial fields.
	 */
	@action
	public assignAttributes(attributes?: Partial<IAddOnsPriceEntityAttributes>) {
		super.assignAttributes(attributes);

		if (attributes) {
			if (attributes.price !== undefined) {
				this.price = attributes.price;
			}
			if (attributes.additionalBookingOptionId !== undefined) {
				this.additionalBookingOptionId = attributes.additionalBookingOptionId;
			}
			if (attributes.additionalBookingOption !== undefined) {
				if (attributes.additionalBookingOption === null) {
					this.additionalBookingOption = attributes.additionalBookingOption;
				} else if (attributes.additionalBookingOption instanceof Models.AdditionalBookingOptionEntity) {
					this.additionalBookingOption = attributes.additionalBookingOption;
					this.additionalBookingOptionId = attributes.additionalBookingOption.id;
				} else {
					this.additionalBookingOption = new Models.AdditionalBookingOptionEntity(
						attributes.additionalBookingOption,
					);
					this.additionalBookingOptionId = this.additionalBookingOption.id;
				}
			}
			if (attributes.defaultPricingId !== undefined) {
				this.defaultPricingId = attributes.defaultPricingId;
			}
			if (attributes.defaultPricing !== undefined) {
				if (attributes.defaultPricing === null) {
					this.defaultPricing = attributes.defaultPricing;
				} else if (attributes.defaultPricing instanceof Models.DefaultPricingEntity) {
					this.defaultPricing = attributes.defaultPricing;
					this.defaultPricingId = attributes.defaultPricing.id;
				} else {
					this.defaultPricing = new Models.DefaultPricingEntity(attributes.defaultPricing);
					this.defaultPricingId = this.defaultPricing.id;
				}
			}
			if (attributes.userId !== undefined) {
				this.userId = attributes.userId;
			}
			if (attributes.user !== undefined) {
				if (attributes.user === null) {
					this.user = attributes.user;
				} else if (attributes.user instanceof Models.UserEntity) {
					this.user = attributes.user;
					this.userId = attributes.user.id;
				} else {
					this.user = new Models.UserEntity(attributes.user);
					this.userId = this.user.id;
				}
			}
		}
	}
}
